import { createAsyncThunk } from "@reduxjs/toolkit";
import type { Dependencies } from "@redux/store";
import type { AppState } from "@redux/app-state";
import { clearAccessTokenCookie } from "@utils/access-token-cookie";
import { clearImpersonationMarker } from "@shared-kernel/core/impersonation/impersonation-marker";
import { syncSession } from "./sync-session";

// Ends an impersonation session and resumes the admin's own session.
//
// Impersonation only swapped the access token; the admin's refresh token stayed in
// its cookie the whole time. Clearing the impersonation access token drops its
// `originalAdminId` claim (so the interceptor's refresh guard no longer blocks),
// then syncSession's first request 401s and the interceptor mints a fresh admin
// access token from the admin refresh token.
//
// Returns true if the admin session is active again, false if it could not be
// restored (admin refresh token gone/expired → caller should terminate/redirect).
export const returnToAdminSession = createAsyncThunk<boolean, void, { state: AppState; extra: Partial<Dependencies> }>(
  "session/returnToAdminSession",
  async (_: void, { dispatch }) => {
    // Before the sync, not after: the admin is leaving on their own terms, and a
    // marker still standing would make the sync read this as an expiry and toast.
    clearImpersonationMarker();
    clearAccessTokenCookie();
    const result = await dispatch(syncSession());
    return syncSession.fulfilled.match(result) && result.payload.status === "active";
  },
);
